import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ScreenLayout, Header, FormHeader } from '../components';
import { Button } from '../components/design-system';
import { BusinessUserPicker } from '../components/BusinessUserPicker';
import { DataListFieldRenderer, isLocalUri } from '../components/data-lists/DataListFieldRenderer';
import { apiService, DataList, DataListColumn } from '../services/api';
import { useAppStore } from '../store/appStore';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { useLocale } from '../contexts/LocaleContext';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { useThemedStyles } from '../hooks/useThemedStyles';

interface DataListRecordFormScreenProps {
  listId?: string;
  recordId?: string;
}

const MEDIA_TYPES: DataListColumn['type'][] = ['image', 'video', 'voice'];

export const DataListRecordFormScreen: React.FC<DataListRecordFormScreenProps> = ({ listId, recordId }) => {
  const styles = useThemedStyles(getStyles);
  const { currentBusiness } = useAppStore();
  const { navigateTo, activeTab, setActiveTab, showNotification } = useAppContext();
  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);
  const { t } = useLocale();
  const rtl = useRTLStyles();

  const isEdit = !!recordId;
  const [list, setList] = useState<DataList | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [values, setValues] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [assignedUserIds, setAssignedUserIds] = useState<string[]>([]);
  const [userPickerOpen, setUserPickerOpen] = useState(false);

  const sortedColumns: DataListColumn[] = React.useMemo(() => {
    if (!list) return [];
    return [...list.columns].sort((a, b) => a.order - b.order);
  }, [list]);

  const load = useCallback(async () => {
    if (!listId) return;
    try {
      setLoading(true);
      const listRes = await apiService.getDataList(listId);
      if (listRes.statusCode === 1000 && listRes.data) {
        setList(listRes.data);
      } else {
        showNotification('error', t('dataLists.notifications.errorTitle'), t('dataLists.recordForm.loadFailed'));
        return;
      }
      if (recordId) {
        const recRes = await apiService.getDataListRecord(listId, recordId);
        if (recRes.statusCode === 1000 && recRes.data) {
          setValues(recRes.data.values || {});
        } else {
          showNotification('error', t('dataLists.notifications.errorTitle'), t('dataLists.recordForm.loadFailed'));
        }
      }
    } catch (err: any) {
      showNotification('error', t('dataLists.notifications.errorTitle'), err?.message || t('dataLists.recordForm.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [listId, recordId, showNotification, t]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listId, recordId]);

  const setFieldValue = (columnId: string, value: any) => {
    setValues((prev) => ({ ...prev, [columnId]: value }));
    setErrors((prev) => {
      if (!prev[columnId]) return prev;
      const next = { ...prev };
      delete next[columnId];
      return next;
    });
  };

  /** Client-side validation mirroring the backend's constraint set — a first line of feedback;
   * the server remains the source of truth and its errors surface on save too. */
  const validate = (): boolean => {
    const next: Record<string, string> = {};
    for (const col of sortedColumns) {
      const val = values[col.id];
      const constraints = col.constraints || {};
      const isEmpty = val == null || val === '';
      if (constraints.required && isEmpty) {
        next[col.id] = t('dataLists.recordForm.validation.required');
        continue;
      }
      if (isEmpty) continue;
      if (col.type === 'number') {
        const num = Number(val);
        if (Number.isNaN(num)) {
          next[col.id] = t('dataLists.recordForm.validation.notNumber');
          continue;
        }
        if (constraints.min != null && num < constraints.min) next[col.id] = t('dataLists.recordForm.validation.min', { min: constraints.min });
        if (constraints.max != null && num > constraints.max) next[col.id] = t('dataLists.recordForm.validation.max', { max: constraints.max });
      }
      if (col.type === 'object' && typeof val === 'string' && val.trim()) {
        try {
          JSON.parse(val);
        } catch {
          next[col.id] = t('dataLists.recordForm.validation.invalidJson');
        }
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  /** Coerce staged form values into their persisted shapes (JSON-parse object columns,
   * number-cast numeric columns). Media local URIs are stripped here — they get uploaded and
   * PATCHed in the two-phase flow after the record id is known. */
  const buildValues = (opts: { includeMedia: boolean }): Record<string, any> => {
    const out: Record<string, any> = {};
    for (const col of sortedColumns) {
      const val = values[col.id];
      if (val == null || val === '') continue;
      if (MEDIA_TYPES.includes(col.type)) {
        if (isLocalUri(val)) {
          if (!opts.includeMedia) continue; // handled in phase 2
        }
        out[col.id] = val;
        continue;
      }
      if (col.type === 'object' && typeof val === 'string') {
        try {
          out[col.id] = JSON.parse(val);
        } catch {
          out[col.id] = val;
        }
        continue;
      }
      if (col.type === 'number') {
        const num = Number(val);
        out[col.id] = Number.isNaN(num) ? val : num;
        continue;
      }
      out[col.id] = val;
    }
    return out;
  };

  const pendingMediaColumns = () =>
    sortedColumns.filter((col) => MEDIA_TYPES.includes(col.type) && isLocalUri(values[col.id]));

  const uploadPendingMedia = async (targetListId: string, targetRecordId: string): Promise<Record<string, string>> => {
    const uploaded: Record<string, string> = {};
    for (const col of pendingMediaColumns()) {
      const uri: string = values[col.id];
      const name = `${col.id}_${Date.now()}`;
      const mimeType =
        col.type === 'image' ? 'image/jpeg' : col.type === 'video' ? 'video/mp4' : 'audio/mpeg';
      const res = await apiService.uploadDataListRecordFile(targetListId, targetRecordId, col.id, uri, name, mimeType);
      if (res.statusCode === 1000 && res.data?.url) {
        uploaded[col.id] = res.data.url;
      }
    }
    return uploaded;
  };

  const handleSave = async () => {
    if (!listId) return;
    if (!validate()) {
      showNotification('error', t('dataLists.notifications.errorTitle'), t('dataLists.recordForm.validation.title'));
      return;
    }
    try {
      setSaving(true);

      if (isEdit && recordId) {
        // Editing: media already uploaded immediately by the renderer, so all values are final.
        const res = await apiService.updateDataListRecord(listId, recordId, { values: buildValues({ includeMedia: true }) });
        if (res.statusCode !== 1000) throw new Error(res.errors?.[0]?.message || t('dataLists.recordForm.saveFailed'));
        if (assignedUserIds.length) {
          await apiService.assignDataListRecord(listId, recordId, { userIds: assignedUserIds });
        }
        showNotification('success', t('dataLists.notifications.successTitle'), t('dataLists.recordForm.updateSuccess'));
        navigateTo('data-list-records', listId);
      } else {
        // Phase 1: create with non-media values to obtain a record id.
        const createRes = await apiService.createDataListRecord(listId, { values: buildValues({ includeMedia: false }) });
        if (createRes.statusCode !== 1000 || !createRes.data) {
          throw new Error(createRes.errors?.[0]?.message || t('dataLists.recordForm.saveFailed'));
        }
        const newRecordId = createRes.data._id;

        // Phase 2: upload pending media files, then PATCH the resulting URLs in.
        const pending = pendingMediaColumns();
        if (pending.length > 0) {
          try {
            const uploadedUrls = await uploadPendingMedia(listId, newRecordId);
            if (Object.keys(uploadedUrls).length > 0) {
              await apiService.updateDataListRecord(listId, newRecordId, { values: uploadedUrls });
            }
          } catch (uploadErr) {
            console.error('[DataListRecordFormScreen] Media upload failed:', uploadErr);
            showNotification('error', t('dataLists.notifications.errorTitle'), t('dataLists.recordForm.uploadFailed'));
          }
        }

        if (assignedUserIds.length) {
          await apiService.assignDataListRecord(listId, newRecordId, { userIds: assignedUserIds });
        }

        showNotification('success', t('dataLists.notifications.successTitle'), t('dataLists.recordForm.createSuccess'));
        navigateTo('data-list-records', listId);
      }
    } catch (err: any) {
      showNotification('error', t('dataLists.notifications.errorTitle'), err?.message || t('dataLists.recordForm.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications
          showMessages
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      formHeader={
        <FormHeader
          title={isEdit ? t('dataLists.recordForm.editTitle') : t('dataLists.recordForm.createTitle')}
          onBack={() => navigateTo('data-list-records', listId)}
          actions={[
            {
              icon: 'checkmark',
              primary: true,
              onPress: handleSave,
              loading: saving,
              disabled: saving,
              accessibilityLabel: 'Save',
            },
          ]}
        />
      }
    >
      <View style={styles.container}>
        {loading ? (
          <View style={styles.centerContainer}>
            <ActivityIndicator size="large" color={colors.primary} />
          </View>
        ) : (
          <ScrollView style={styles.content} contentContainerStyle={styles.contentInner} showsVerticalScrollIndicator={false}>
            {sortedColumns.map((col) => (
              <DataListFieldRenderer
                key={col.id}
                column={col}
                value={values[col.id]}
                onChange={(v) => setFieldValue(col.id, v)}
                error={errors[col.id]}
                businessId={currentBusiness?._id || ''}
                listId={listId || ''}
                recordId={recordId}
              />
            ))}

            {/* Optional per-record assignment */}
            <Text style={[styles.sectionTitle, { textAlign: rtl.textStart }]}>{t('dataLists.recordForm.assignedSection')}</Text>
            <TouchableOpacity style={[styles.selectButton, { flexDirection: rtl.rowDirection }]} onPress={() => setUserPickerOpen(true)}>
              <Text style={[styles.selectButtonText, !assignedUserIds.length && styles.selectButtonPlaceholder, { textAlign: rtl.textStart }]}>
                {assignedUserIds.length ? t('dataLists.schema.usersSelected', { count: assignedUserIds.length }) : t('dataLists.recordForm.assignUser')}
              </Text>
              <Ionicons name="chevron-down" size={18} color={colors.textLight} />
            </TouchableOpacity>

            <Button
              title={saving ? t('dataLists.recordForm.saving') : isEdit ? t('dataLists.recordForm.save') : t('dataLists.recordForm.create')}
              onPress={handleSave}
              disabled={saving}
              loading={saving}
              style={styles.saveButton}
            />
          </ScrollView>
        )}

        <BusinessUserPicker
          visible={userPickerOpen}
          onClose={() => setUserPickerOpen(false)}
          onConfirm={(ids) => setAssignedUserIds(ids)}
          mode="multiple"
          source="users"
          selectedUserIds={assignedUserIds}
          businessId={currentBusiness?._id}
        />
      </View>
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.lightGray },
  content: { flex: 1 },
  contentInner: { padding: spacing.lg, paddingBottom: Platform.OS !== 'web' ? 120 : spacing.xl },
  centerContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  sectionTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
    marginTop: spacing.lg,
    marginBottom: spacing.sm,
  },
  selectButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.md,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: designTokens.borderRadius.md,
    backgroundColor: colors.background.surface,
  },
  selectButtonText: { flex: 1, fontSize: typography.fontSize.base, color: colors.text.primary, fontFamily: typography.fontFamily.regular },
  selectButtonPlaceholder: { color: colors.textLight },
  saveButton: { marginTop: spacing.xl },
});
